import {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {LogOut, ChevronRight, Home, Handshake, ArrowRightLeft} from 'lucide-react';
import {Badge} from '@sakura-grp/ui/badge';
import {isWithinInterval, parseISO, startOfDay} from 'date-fns';
import {formatDate} from '@sakura-grp/lib';
import {useAuthStore} from '@/stores/auth';
import {useHostessStore} from '@/stores/hostess';
import {getClient} from '@sakura-grp/database';
import AppPicture from '~/components/react/AppPicture';
import {global_logo_svg} from '@/assets/image-size-mapping';
import {getHostessProfileImageUrlOrPlaceholder} from '@/lib/storage-utils';

interface StoreAssignment {
  id: string;
  store_id: string;
  assigned_from: string;
  assigned_to: string | null;
  transfer_reason: string | null;
  store: {store_name: string} | null;
}

interface HelpShift {
  id: string;
  store_id: string;
  shift_from: string;
  shift_to: string;
  notes: string | null;
  store: {store_name: string} | null;
}

function isHelpActive(shift: HelpShift): boolean {
  const today = startOfDay(new Date());
  const from = startOfDay(parseISO(shift.shift_from));
  const to = startOfDay(parseISO(shift.shift_to));
  return isWithinInterval(today, {start: from, end: to});
}

export default function Mypage() {
  const navigate = useNavigate();
  const {signOut: signOutStore} = useAuthStore();
  const {clear: clearHostess, hostess} = useHostessStore();
  const [assignments, setAssignments] = useState<StoreAssignment[]>([]);
  const [helpShifts, setHelpShifts] = useState<HelpShift[]>([]);

  const profileImageUrl = getHostessProfileImageUrlOrPlaceholder(hostess?.profile_image_path);

  // 所属店舗 + ヘルプシフトを取得
  useEffect(() => {
    if (!hostess?.id) return;

    const fetchStoreData = async () => {
      const supabase = getClient();

      const [assignmentsResult, helpShiftsResult] = await Promise.all([
        supabase
          .from('hostess_store_assignments')
          .select('id, store_id, assigned_from, assigned_to, transfer_reason, store:stores(store_name)')
          .eq('hostess_id', hostess.id)
          .order('assigned_from', {ascending: false}),
        supabase
          .from('hostess_help_shifts')
          .select('id, store_id, shift_from, shift_to, notes, store:stores(store_name)')
          .eq('hostess_id', hostess.id)
          .is('cancelled_at', null)
          .order('shift_from', {ascending: false}),
      ]);

      if (assignmentsResult.data) {
        setAssignments(assignmentsResult.data as unknown as StoreAssignment[]);
      }
      if (helpShiftsResult.data) {
        setHelpShifts(helpShiftsResult.data as unknown as HelpShift[]);
      }
    };

    fetchStoreData();
  }, [hostess?.id]);

  const handleSignOut = async () => {
    try {
      const supabase = getClient();
      await supabase.auth.signOut();
      signOutStore();
      clearHostess();
      navigate('/sign-in', {replace: true});
    } catch (error) {
      console.error('サインアウトエラー:', error);
      navigate('/sign-in', {replace: true});
    }
  };

  const currentAssignment = assignments.find((a) => !a.assigned_to);
  const pastAssignments = assignments.filter((a) => a.assigned_to);

  return (
    <main className="h-full flex flex-col">
      {/* ヘッダー */}
      <div className="px-lg h-3xl flex items-center pb-[1.4rem] flex-shrink-0">
        <h1 className="text-3xl">マイページ</h1>
      </div>

      {/* コンテンツエリア */}
      <div className="flex-1 overflow-y-auto border-t bg-white">
        {/* プロフィールセクション */}
        <div className="px-lg py-lg border-b">
          <div className="flex items-center gap-md">
            <figure className="relative flex size-[5rem] flex-none overflow-hidden rounded-full border-2 border-brand/20 bg-brand/10">
              <img
                src={profileImageUrl}
                alt={hostess?.name || ''}
                className="w-full h-full object-cover"
              />
            </figure>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-stone-500">ログイン中</p>
              <p className="text-xl font-bold truncate">{hostess?.name || '---'}</p>
              <p className="text-sm text-brand">{hostess?.role || 'ホステス'}</p>
            </div>
          </div>
        </div>

        {/* 所属店舗セクション */}
        <div className="px-lg py-lg border-b">
          <h2 className="text-sm font-bold text-stone-600 mb-md">所属店舗</h2>
          <div className="space-y-sm">
            {/* 在籍 */}
            {currentAssignment && (
              <div className="flex items-center gap-md px-sm py-sm rounded-lg bg-stone-50">
                <div className="size-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                  <Home className="size-4 text-emerald-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium">{currentAssignment.store?.store_name || '不明'}</p>
                  <p className="text-xs text-stone-400">{formatDate(currentAssignment.assigned_from)}〜</p>
                </div>
                <Badge variant="secondary" className="text-2xs bg-emerald-100 text-emerald-700 flex-shrink-0">在籍</Badge>
              </div>
            )}

            {/* ヘルプシフト */}
            {helpShifts.map((shift) => {
              const active = isHelpActive(shift);
              return (
                <div key={shift.id} className="flex items-center gap-md px-sm py-sm rounded-lg bg-stone-50">
                  <div className="size-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <Handshake className="size-4 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium">{shift.store?.store_name || '不明'}</p>
                    <p className="text-xs text-stone-400">{formatDate(shift.shift_from)}〜{formatDate(shift.shift_to)}</p>
                  </div>
                  <Badge variant="secondary" className={`text-2xs flex-shrink-0 ${active ? 'bg-blue-100 text-blue-700' : 'bg-stone-100 text-stone-500'}`}>
                    {active ? 'ヘルプ中' : 'ヘルプ'}
                  </Badge>
                </div>
              );
            })}

            {/* 移籍履歴 */}
            {pastAssignments.map((a) => (
              <div key={a.id} className="flex items-center gap-md px-sm py-sm rounded-lg bg-stone-50 opacity-60">
                <div className="size-8 rounded-full bg-stone-100 flex items-center justify-center flex-shrink-0">
                  <ArrowRightLeft className="size-4 text-stone-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-stone-500">{a.store?.store_name || '不明'}</p>
                  <p className="text-xs text-stone-400">{formatDate(a.assigned_from)}〜{formatDate(a.assigned_to!)}</p>
                </div>
                <span className="text-2xs text-stone-400 flex-shrink-0">移籍</span>
              </div>
            ))}

            {assignments.length === 0 && helpShifts.length === 0 && (
              <p className="text-sm text-stone-400">所属情報がありません</p>
            )}
          </div>
        </div>

        {/* メニューリスト */}
        <div className="divide-y">
          <button
            type="button"
            onClick={handleSignOut}
            className="w-full px-lg py-md flex items-center gap-md text-left hover:bg-red-50 transition-colors"
          >
            <div className="size-10 rounded-full bg-red-100 flex items-center justify-center">
              <LogOut className="size-5 text-red-600" />
            </div>
            <div className="flex-1">
              <p className="font-medium text-red-600">ログアウト</p>
              <p className="text-sm text-stone-500">アプリからサインアウトします</p>
            </div>
            <ChevronRight className="size-5 text-red-400" />
          </button>
        </div>

        {/* フッター */}
        <div className="px-lg py-xl mt-auto">
          <div className="flex flex-col items-center gap-md text-center">
            <AppPicture
              img={global_logo_svg}
              sources={[['default', {}]]}
              pictureClass="w-[10rem] opacity-30"
              alt="SAKURA GROUP HOLDINGS."
              imgClass="w-full"
            />
            <p className="text-xs text-stone-400">
              Sakura Group HD Hostess App
              <br />
              {__APP_VERSION__}
            </p>
          </div>
        </div>
      </div>
    </main>
  );
}
